import { Alert, Avatar, Box, CircularProgress, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridToolbarQuickFilter
} from '@mui/x-data-grid';
import { AppDispatch, RootState } from 'app/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearManagers,
  fetchManagers
} from '../../slices/NdisSlices/managersList/ManagersListSlice';
import { useClientContext } from './ProfileMainPage';

// DataGrid Columns
const workerColumns: GridColDef[] = [
  {
    field: 'avatar',
    headerName: '',
    width: 80,
    renderCell: (params) => (
      <Avatar src={params.value} alt="Avatar" sx={{ width: 40, height: 40 }} />
    ),
    sortable: false,
    filterable: false
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    minWidth: 150
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 2,
    minWidth: 200
  }
];

function CustomToolbar() {
  return (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: 'background.paper',
        borderBottom: '1px solid #e0e0e0'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', ml: 2 }}>
        Workers Directory
      </Typography>
      <GridToolbarQuickFilter
        placeholder="Search workers..."
        debounceMs={500}
        quickFilterParser={(value) => value.split(' ')}
        sx={{
          width: '100%',
          maxWidth: 300,
          '& .MuiInputBase-root': {
            borderRadius: '5px',
            paddingLeft: '15px',
            bgcolor: '#f4f4f4',
            border: '1px solid #d6d6d6'
          }
        }}
      />
    </Box>
  );
}

const WorkersList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedClient } = useClientContext();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  // Redux state
  const workers = useSelector(
    (state: RootState) => state.managersList.fullStaff?.participants_list['Support worker'] || []
  );
  const loading = useSelector((state: RootState) => state.managersList.loading);
  const error = useSelector((state: RootState) => state.managersList.error);
  const selectedProjectId = useSelector(
    (state: RootState) => state.homeTilesServices.selectedProjectId
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected.selectedServiceTitle
  );
  const userDetails = localStorage.getItem('user-details');
  const parsedUserDetails = userDetails ? JSON.parse(userDetails) : null;
  const email = parsedUserDetails?.email || '';
  const roles = parsedUserDetails?.role ? [parsedUserDetails.role] : [''];

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    if (selectedClient && selectedProjectId && selectedServiceTitle && email && roles.length > 0) {
      const payload = {
        group_id: selectedProjectId,
        service_name_id: selectedServiceTitle,
        nested_component_id: 'Workers',
        email,
        roles,
        client_name: selectedClient.label
      };
      dispatch(fetchManagers(payload));
    }
    return () => {
      dispatch(clearManagers());
    };
  }, [dispatch, selectedClient, selectedProjectId, selectedServiceTitle, email, roles]);

  const theme = createTheme({
    palette: {
      primary: { main: '#607d8b', contrastText: '#ffffff' },
      secondary: { main: '#b0bec5' },
      background: { default: '#f5f5f5', paper: '#ffffff' }
    },
    typography: { fontFamily: 'Roboto, Arial' }
  });

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: 700,
          width: '100%',
          bgcolor: 'background.default',
          p: 3,
          borderRadius: 3,
          boxShadow: 2
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!selectedClient && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please select a client to view workers.
          </Alert>
        )}
        {selectedClient && !loading && !error && (
          <DataGrid
            rows={workers}
            columns={workerColumns}
            getRowId={(row) => row.id}
            components={{ Toolbar: CustomToolbar }}
            checkboxSelection
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#e8eaf6',
                color: '#37474f',
                fontSize: '15px',
                fontWeight: 'bold'
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                bgcolor: '#f9f9f9'
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                bgcolor: '#ffffff'
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: '#cfd8dc'
              },
              '& .MuiDataGrid-cell': {
                fontSize: '14px'
              },
              '& .MuiDataGrid-footerContainer': {
                bgcolor: '#e8eaf6',
                borderTop: '1px solid #d0d0d0'
              }
            }}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default WorkersList;
